import { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '@/services/auth';
import useAuthStore from '@/stores/auth';
import { Shield, Zap, BarChart3, Eye, EyeOff } from 'lucide-react';
import Button from '@/components/ui/Button';

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submitLock = useRef(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitLock.current) return;
    setError('');
    submitLock.current = true;
    setLoading(true);
    try {
      const { data } = await login(email, password);
      setAuth(data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password.');
    } finally {
      submitLock.current = false;
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left — Branding panel */}
      <div className="hidden flex-1 flex-col justify-between bg-gradient-to-br from-[#0f3d3a] via-[#115e59] to-[#134e4a] p-12 lg:flex">
        <div>
          {/* Logo kit v1: full white wordmark on the dark brand panel. */}
          <img src="/brand/cervica/cervica-horizontal-white.svg" alt="Cervica" className="h-10 w-auto" />
          <p className="mt-2 text-[14px] text-teal-200/60">
            Chiropractic EHR & Practice Management
          </p>
        </div>

        <div className="space-y-8">
          <h2 className="text-[32px] font-bold leading-tight text-white">
            The modern EHR<br />
            built for chiropractors.
          </h2>
          <div className="space-y-5">
            <Feature
              icon={Zap}
              title="5-Click SOAP Notes"
              description="Macro-driven documentation that generates complete clinical narratives in seconds."
            />
            <Feature
              icon={BarChart3}
              title="Multi-Location Intelligence"
              description="Side-by-side analytics, cross-location scheduling, and unified reporting."
            />
            <Feature
              icon={Shield}
              title="Practice access controls"
              description="Role-based access and audit logging support your practice workflows."
            />
          </div>
        </div>

        <p className="text-[11px] text-teal-300/30">
          &copy; {new Date().getFullYear()} Cervica EHR LLC. All rights reserved.
        </p>
      </div>

      {/* Right — Login form */}
      <div className="flex flex-1 items-center justify-center bg-[#FAFAFA] px-6 lg:max-w-[520px]">
        <div className="w-full max-w-[380px]">
          {/* Mobile logo */}
          <div className="mb-8 lg:hidden">
            <img src="/brand/cervica/cervica-horizontal-color.svg" alt="Cervica" className="h-9 w-auto" />
          </div>

          <div className="mb-8">
            <h1 className="text-[24px] font-bold text-gray-900">Welcome back</h1>
            <p className="mt-1 text-[14px] text-gray-500">
              Sign in to your practice account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-[11px] font-bold text-red-600">!</span>
                {error}
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@practice.com"
                className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[14px] text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-[14px] text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1 text-right">
                <Link
                  to="/forgot-password"
                  className="text-[12px] font-medium text-primary-600 hover:text-primary-700"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl py-2.5 text-[14px]"
              isLoading={loading}
            >
              Sign in
            </Button>

            <p className="text-center text-[13px] text-gray-500">
              New practice?{' '}
              <Link to="/register" className="font-medium text-primary-600 hover:text-primary-700">
                Start a 30-day free trial
              </Link>
            </p>
          </form>

          <p className="mt-8 text-center text-[11px] text-gray-400">
            Cervica EHR LLC &middot; Practice account sign-in
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
        <Icon className="h-5 w-5 text-teal-300" />
      </div>
      <div>
        <p className="text-[14px] font-semibold text-white">{title}</p>
        <p className="mt-0.5 text-[13px] leading-relaxed text-teal-200/60">{description}</p>
      </div>
    </div>
  );
}
